import {
  Component,
  Directive,
  ViewEncapsulation,
  Input,
  ViewChild,
  ContentChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  EventEmitter,
  Output,
} from "@angular/core";

import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";
import { ReplaySubject, Subscription } from "rxjs";

@Directive({
  selector: "ui-dialog-header",
})
export class DialogHeaderDirective {}

@Directive({
  selector: "ui-dialog-body",
})
export class DialogBodyDirective {}

@Directive({
  selector: "ui-dialog-footer",
})
export class DialogFooterDirective {}

type Action = {
  open: boolean;
  detached: boolean;
};

/**
 * Dialogs inform users about a task and can contain critical information, require decisions, or involve multiple tasks.
 *
 * Should be used for:
 *
 * * Errors that block an app’s normal operation
 * * Critical information that requires a specific user task, decision, or acknowledgement
 *
 * If you detach the dialog it will occupy the entire screen when activated, if not attached it will only occupy the content area of the <ui-dialog> element.
 *
 * Dialogs are purposefully interruptive, so they should be used sparingly.
 */
@Component({
  selector: "ui-dialog",
  templateUrl: "./dialog.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        position: relative;
        display: block;
      }
      :host.open::before {
        content: " ";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.5);
      }
      :host:not([detached]).open dialog {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    `,
  ],
})
export class DialogComponent implements AfterViewInit, OnDestroy {
  /**
   * Whether the dialog should be opened or closed.
   *
   * Default is `false`.
   */
  @Input()
  get open(): boolean {
    return this._open;
  }
  set open(value: BooleanInput) {
    this._open = coerceBooleanProperty(value);
    this.$actions.next({
      open: this.open,
      detached: this.detached,
    });
  }
  private _open = false;
  @Output() readonly openChange = new EventEmitter<boolean>();
  /**
   * Whether the dialog should be shown a top of the entire page or not.
   *
   * Default is `false`.
   */
  @Input()
  get detached(): boolean {
    return this._detached;
  }
  set detached(value: BooleanInput) {
    this._detached = coerceBooleanProperty(value);
    this.$actions.next({
      open: this.open,
      detached: this.detached,
    });
  }
  private _detached = false;

  // Whether the dialog element has been detected or not
  _initialized = false;

  $actions: ReplaySubject<Action> = new ReplaySubject();
  actionsSubscription = Subscription.EMPTY;

  @ViewChild("dialogElement") dialogElement!: ElementRef<HTMLDialogElement>;

  @ContentChild(DialogHeaderDirective) dialogHeaderDirective?: DialogHeaderDirective;
  @ContentChild(DialogBodyDirective) dialogBodyDirective?: DialogBodyDirective;
  @ContentChild(DialogFooterDirective) dialogFooterDirective?: DialogBodyDirective;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    this.actionsSubscription = this.$actions.asObservable().subscribe((action) => {
      this.dialogElement.nativeElement.close();
      if (action.open) {
        if (action.detached) this.dialogElement.nativeElement.showModal();
        else {
          this.elementRef.nativeElement.classList.add("open");
          this.dialogElement.nativeElement.show();
        }
      } else this.elementRef.nativeElement.classList.remove("open");
    });
  }

  ngOnDestroy() {
    this.$actions.complete();
    this.actionsSubscription.unsubscribe();
  }

  onClose() {
    this.open = false;
    this.openChange.emit(false);
  }
}
